import moment from 'moment';
import { useMutation, useQueryClient } from 'react-query';
import { checkIsFutureDate } from 'shared-utils';
import { useToast } from 'src/context/ToastProvider';
import React, { FormEvent, useEffect, useState } from 'react';
import { Button, Label, Modal, Spinner, TextInput } from 'flowbite-react';
import { UpdateCertificationDetailsInput, UserCertificationDetail } from 'types';
import { createUserCertificateFn, editUserCertificateFn } from 'src/apis/profile';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	userCertificates: UserCertificationDetail;
	isEditing?: boolean;
	selectedCertficateId: string;
};

const EditProfileCertificateModal = ({ isOpen, selectedCertficateId, onClose, userCertificates, isEditing }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [certificateId, setCertificateId] = useState('');
	const [dateError, setDateError] = useState<string>('');
	const [userCertificate, setUserCertificate] = useState<UpdateCertificationDetailsInput>({
		updateInfo: {
			cretificateTitle: '',
			cretificateIssuerName: '',
			certificateUniqueId: '',
			issuedOn: new Date(),
		},
	});

	const editCertificateDetailsFn = useMutation({
		mutationKey: 'editCertificateDetail',
		mutationFn: editUserCertificateFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Certificate detail updated successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const createCertificateDetailsFn = useMutation({
		mutationKey: 'addCertificateDetail',
		mutationFn: createUserCertificateFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Certificate detail added successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (userCertificate.updateInfo.issuedOn) {
			if (checkIsFutureDate(moment(userCertificate.updateInfo.issuedOn))) {
				setDateError('Start date cannot be a future date');
				return;
			}
		}

		if (isEditing) {
			editCertificateDetailsFn.mutate({ id: certificateId, data: userCertificate });
		} else {
			createCertificateDetailsFn.mutate(userCertificate);
		}
	};

	useEffect(() => {
		setUserCertificate({
			updateInfo: {
				cretificateTitle: userCertificates.cretificateTitle || '',
				cretificateIssuerName: userCertificates.cretificateIssuerName || '',
				certificateUniqueId: userCertificates.certificateUniqueId || '',
				issuedOn: userCertificates.issuedOn ? moment(userCertificates.issuedOn).toDate() : new Date(),
			},
		});
		setCertificateId(selectedCertficateId || '');
	}, [userCertificates, selectedCertficateId]);

	useEffect(() => {
		setDateError('');
	}, [isOpen]);

	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size="lg" className="!h-full backdrop-blur-[4px]">
			<Modal.Header className="rounded-t-md border-b-0 dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:text-white">Certificate</p>
				</div>
			</Modal.Header>
			<form className="dark:text-white" onSubmit={handleSubmit}>
				<Modal.Body className="dark:bg-[#1F2A37]">
					<div className="">
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label className="font-inter text-[14px] font-[400] dark:text-white" htmlFor="name" value="Name" />
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="name"
								name="name"
								value={userCertificate.updateInfo.cretificateTitle || ''}
								onChange={e =>
									setUserCertificate({
										updateInfo: {
											...userCertificate.updateInfo,
											cretificateTitle: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="organisation"
									value="Organisation"
								/>
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="organisation"
								name="organisation"
								value={userCertificate.updateInfo.cretificateIssuerName || ''}
								onChange={e =>
									setUserCertificate({
										updateInfo: {
											...userCertificate.updateInfo,
											cretificateIssuerName: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="credential"
										value="Credential ID/URL"
									/>
								</div>
								<TextInput
									style={{
										fontFamily: "'Inter', sans-serif",
									}}
									type="text"
									id="credential"
									name="credential"
									value={userCertificate.updateInfo.certificateUniqueId || ''}
									onChange={e =>
										setUserCertificate({
											updateInfo: {
												...userCertificate.updateInfo,
												certificateUniqueId: e.target.value,
											},
										})
									}
								/>
							</div>
						</div>
						<div className="mb-[8px]">
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="issued_date"
										value="Issued On"
									/>
									<span className="text-red-600">*</span>
								</div>
								<div className="flex">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="issued_date"
										name="issued_date"
										value={moment(userCertificate.updateInfo.issuedOn).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setUserCertificate({
												updateInfo: {
													...userCertificate.updateInfo,
													issuedOn: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>
						</div>
					</div>
					{dateError && <div className="text-xs font-bold text-red-500">{dateError}</div>}
				</Modal.Body>
				<Modal.Footer className="rounded-b-md border-t-0 dark:bg-[#18222D]">
					<div className="flex w-full justify-end">
						<div className="">
							<Button
								className="bg-[#7777E7]"
								type="submit"
								disabled={
									!userCertificate.updateInfo.cretificateTitle ||
									!userCertificate.updateInfo.cretificateIssuerName ||
									!userCertificate.updateInfo.issuedOn ||
									editCertificateDetailsFn.isLoading ||
									createCertificateDetailsFn.isLoading
								}
							>
								{isEditing ? (
									<>{editCertificateDetailsFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								) : (
									<>{createCertificateDetailsFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								)}
							</Button>
						</div>
					</div>
				</Modal.Footer>
			</form>
		</Modal>
	);
};

export default EditProfileCertificateModal;
